<template>
  <ToastDialog :title="toastTitle" :message="toastMessage" :error="toastError" />
  <div class="question-detail-expert d-flex h-100 no-grow">
    <div v-if="isLoading" class="loading-overlay">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Caricamento...</span>
      </div>
      <div class="loading-text">Caricamento in corso...</div>
    </div>
    <div v-if="isChangingState" class="loading-overlay">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Salvataggio in corso...</span>
      </div>
      <div class="loading-text">Salvataggio in corso...</div>
    </div>

    <div class="left-toggle d-flex align-items-center" @click="toggleLeftPanel">
      <i :class="leftPanelVisible ? 'bi bi-chevron-left' : 'bi bi-chevron-right'"></i>
    </div>

    <SimpleModal v-model="showConfirmModal"
      title="Procedendo, la risposta al quesito sarà inviata alla Stazione Appaltante" :buttons="['Modifica', 'Invia']"
      @chose="handleConfirmChoice" />

    <SimpleModal v-model="showSuccessModal" title="Quesito evaso" :buttons="['Ok']" @chose="handleSuccessModalChoice" />

    <SimpleModal v-model="showUOCDecisionModal" title="Conferma decisione UOC" :buttons="['Annulla', 'Conferma']"
      @chose="handleUOCDecision" />


    <SimpleModal v-model="showMoveModal" title="Conferma spostamento" :buttons="['Annulla', 'Conferma']"
      @chose="handleMove" />

    <SimpleModal v-model="showForwardModal" title="Conferma Riassegnazione Quesito" :buttons="['Annulla', 'Conferma']"
      @chose="handleForward" />

    <!-- Pannello sinistro -->
    <transition name="slide">
      <div v-if="leftPanelVisible" class="left-panel p-3 border-end" style="flex: 1">

        <!-- Switch -->
        <div v-if="showLeftPanelToggle" class="toggle-3d mb-3">
          <input type="checkbox" id="leftViewToggle" class="toggle-switch" v-model="leftView" true-value="topics"
            false-value="articles">
          <label for="leftViewToggle" class="toggle-label">
            <span class="toggle-option toggle-left">D.lgs. 36/2023</span>
            <span class="toggle-option toggle-right">
              {{ commentLabel }}
              <span v-if="comments.length > 0" class="comment-count">{{ comments.length }}</span>
            </span>
          </label>
        </div>

        <div v-else class="mb-3">
        </div>

        <!-- CONTENUTO VARIABILE -->
        <div class="left-content">

          <!-- VISTA ARTICOLI -->
          <div v-if="leftView === 'articles'" class="left-content">
            <ArticlesView class="compact-sidebar" />

            <ModalView v-if="showArticleModal" v-model="showArticleModal" title="Dettaglio Articolo"
              icon="file-earmark-text" @close="closeArticleModal">
              <ArticleView :articleId="modalArticleId" :version="modalVersion"
                :key="selectedArticleId + '-' + modalCounter" />
            </ModalView>
          </div>

          <!-- VISTA COMMENTI -->
          <div v-else-if="leftView === 'topics'" class="comments-view">
            <div class="comments-header d-flex align-items-center justify-content-between mb-3">
              <h5 class="m-0">{{ commentLabel }}</h5>
              <button class="btn btn-sm btn-light text-secondary border-0 d-flex align-items-center"
                @click="exportCommentsPdf">
                Esporta
                <i class="bi bi-arrow-down-short ms-1"></i>
              </button>
            </div>
            <div v-for="comment in comments" :key="comment.id"
              :class="[comment.type_id === 'I' ? 'integration-postit' : 'comment-postit']">
              <div class="comment-header d-flex align-items-center mb-2">
                <div class="avatar me-2">{{ getInitials(comment.user) }}</div>
                <div class="user-info me-auto">
                  <div class="user-name">{{ comment.user.name }} {{ comment.user.surname }}</div>
                  <div class="comment-date">{{ formatDate(comment.created_at) }}</div>
                </div>
              </div>

              <div class="comment-text" :class="{ collapsed: !comment.expanded }" v-html="sanitize(comment.text)"></div>

              <div v-if="needsReadMore(comment)" class="text-end mt-1">
                <button class="btn btn-sm btn-link p-0" @click="toggleComment(comment)">
                  {{ comment.expanded ? 'Chiudi' : 'Leggi di più...' }}
                </button>
              </div>
            </div>
          </div>

        </div>
      </div>
    </transition>

    <!-- Pannello destro -->
    <div class="right-panel p-3" :style="{ flex: leftPanelVisible ? 3 : 1, display: 'flex', flexDirection: 'column' }">

      <!-- Header quesito -->
      <div class="question-header mb-3 border-bottom pb-3">
        <div class="d-flex justify-content-between align-items-center mb-1">
          <h4>{{ question.object }}</h4>

          <div class="d-flex gap-2">
            <!-- Freccia indietro -->
            <button v-if="isExternalUser" class="btn btn-outline-secondary btn-sm" @click="goBackWithFilters"
              title="Torna all'elenco dei quesiti">
              <i class="bi bi-arrow-left"></i>
            </button>

            <button v-if="(isUOCMember || isUOCManager) && !canAssignQuestion" class="btn btn-outline-secondary btn-sm"
              @click="handleBack" title="Indietro">
              <i class="bi bi-arrow-left"></i>
            </button>

            <button v-if="isUOCManager && canAssignQuestion" class="btn btn-outline-secondary btn-sm"
              @click="goBackWithFilters" title="Torna all'elenco dei quesiti">
              <i class="bi bi-card-list"></i>
            </button>

            <button v-if="isUOCManager && canAssignQuestion && question.uoc_session_id"
              class="btn btn-outline-secondary btn-sm" @click="handleBack" title="Torna alla sala UOC">
              <i class="bi bi-diagram-3"></i>
            </button>

            <template v-if="showAssignedActions">
              <button v-if="canEditArticlesAndTopics" class="btn btn-warning btn-sm" :disabled="isSaveDisabled"
                @click="isUOCManager ? saveDraftUoc() : saveArticlesAndTopics()"
                title="Salva modifiche articoli/argomenti">
                <i class="bi bi-floppy"></i>
              </button>
              <button v-if="canShowMoveToUOC" class="btn btn-primary btn-sm" @click="confirmMove('to_uoc')">
                Sposta in UOC
              </button>
              <button v-if="canShowMoveToWaiting" class="btn btn-primary btn-sm" @click="confirmMove('to_waiting')">
                Sposta in Sala Attesa
              </button>
              <button v-if="canIntegrateInAssigned" class="btn btn-primary btn-sm" @click="openCommentEditor(true)"
                :disabled="isIntegrateDisabledInAssigned">
                Integra
              </button>
              <button v-if="isMyQuestionNotMoving" :disabled="!canTakeOver" @click="prendiInCarico"
                class="btn btn-secondary btn-sm" title="Prendi in carico il quesito">
                <i class="bi bi-hand-index-thumb"></i>
              </button>
              <button v-if="isMyQuestionNotMoving && !canEditArticlesAndTopics" class="btn btn-warning btn-sm"
                :disabled="disableSaveDraftBtn" @click="saveDraft" data-bs-toggle="tooltip" title="Salva">
                <i class="bi bi-floppy"></i>
              </button>
              <button v-if="isMyQuestionNotMoving && canReply" class="btn btn-sm btn-primary"
                :disabled="disableReplyBtn" @click="showConfirmModal = true" data-bs-toggle="tooltip" title="Rispondi">
                <i class="bi bi-arrow-right"></i>
              </button>
              <button v-else-if="isMyQuestionNotMoving && !canReply" class="btn btn-sm btn-warning"
                :disabled="disableForwardBtn" @click="showForwardModal = true" data-bs-toggle="tooltip" title="Inoltra">
                <i class="bi bi-arrow-up"></i>
              </button>
            </template>

            <!--<template v-else>
              <button v-if="canEditArticlesAndTopics" class="btn btn-warning btn-sm" :disabled=isSaveDisabled
                @click="saveArticlesAndTopics" data-bs-toggle="tooltip" title="Salva modifiche articoli/argomenti">
                <i class="bi bi-floppy"></i>
              </button>
              <button v-if="canComment" class="btn btn-primary btn-sm" @click="openCommentEditor">
                Commenta
              </button>
              <button v-else-if="isUOCMember" class="btn btn-primary btn-sm" @click="openIntegrationEditor">
                Integra
              </button>
            </template>-->
            <template v-else>

              <button v-if="canRiesamina" class="btn btn-warning btn-sm" title="Riesamina" @click="enableRiesamina">
                <i class="bi bi-pencil-square"></i>
              </button>

              <button v-if="canEditArticlesAndTopics" class="btn btn-warning btn-sm" :disabled="isSaveDisabled"
                @click="isUOCManager ? saveDraftUoc() : saveArticlesAndTopics()"
                title="Salva modifiche articoli/argomenti">
                <i class="bi bi-floppy"></i>
              </button>

              <button v-if="canShowMoveToUOC" class="btn btn-primary btn-sm" @click="confirmMove('to_uoc')">
                Sposta in UOC
              </button>

              <button v-else-if="canShowMoveToWaiting" class="btn btn-secondary btn-sm"
                @click="confirmMove('to_attesa')">
                Sposta in sala d’attesa
              </button>

              <div v-else-if="canShowUOCDecisionDropdown" class="dropdown">
                <button class="btn btn-primary btn-sm dropdown-toggle" data-bs-toggle="dropdown">
                  Decisione UOC
                </button>

                <ul class="dropdown-menu">
                  <li>
                    <button class="dropdown-item" @click="confirmUOCDecision('ASSEVERA_PUBBLICA')" :disabled="isOpinionEmpty">
                      Assevera e pubblica
                    </button>
                  </li>
                  <li>
                    <button class="dropdown-item" @click="confirmUOCDecision('ASSEVERA_NON_PUBBLICA')" :disabled="isOpinionEmpty">
                      Assevera e non pubblicare
                    </button>
                  </li>
                  <li>
                    <button class="dropdown-item text-danger" @click="confirmUOCDecision('NON_ASSEVERA')">
                      Non asseverare
                    </button>
                  </li>
                </ul>
              </div>
              <button v-else-if="canShowComment" class="btn btn-primary btn-sm" @click="openCommentEditor(false)"
                :disabled="isCommentDisabled">
                Commenta
              </button>
              <button v-if="canShowIntegration" class="btn btn-primary btn-sm" @click="openCommentEditor(true)"
                :disabled="isIntegrateDisabledInElse">
                Integra
              </button>

            </template>
          </div>
        </div>

        <div class="text-muted small mb-1 d-flex align-items-center justify-content-between">
          <div class="d-flex gap-2 align-items-center">
            <strong><span>#{{ question.id }}</span></strong>
            <strong><span>{{ question.organization }}</span></strong>
            <!--<strong><span>Friuli Venezia Giulia</span></strong>-->
            <strong><span><i class="bi bi-clock"></i> Data creazione: {{ formatDate(question.created_at)
                }}</span></strong>
            <strong><span><i class="bi bi-clock"></i> Ultima modifica: {{ formatDate(question.updated_at)
                }}</span></strong>
          </div>
        </div>

        <!-- Articoli correlati -->
        <div class="text-muted small mb-1 d-flex align-items-center gap-2 flex-wrap">
          <strong>Articoli correlati:</strong>



          <div class="dropdown" v-if="showAddArticleDropdown">
            <button class="btn btn-primary small-rect-btn" data-bs-toggle="dropdown">+</button>

            <ul class="dropdown-menu p-2" style="max-height:500px; overflow-y:auto; min-width:500px;">
              <li class="mb-2 px-2">
                <input type="text" class="form-control form-control-sm" placeholder="Cerca..." v-model="articleSearch">
              </li>

              <!--<li v-for="item in filteredArticles" :key="item.id" class="form-check">
                <input class="form-check-input" type="checkbox" :id="'article-' + item.id" :value="item.id"
                  v-model.number="selectedArticlesIds">
                <label class="form-check-label ms-2" :for="'article-' + item.id">
                  <strong v-if="item.isSection">{{ item.title }}</strong>
                  <span v-else>{{ item.versions[item.versions.length - 1].title }}</span>
                </label>
              </li>-->
              <template v-if="!isLoading && availableArticles.length">
                <li v-for="(item, index) in filteredArticles" :key="item.id" class="form-check">

                  <template v-if="showCheckbox(item, index)">

                    <input class="form-check-input" type="checkbox" :id="'article-' + item.id" :value="item.id"
                      v-model.number="selectedArticlesIds" @change="item.isSection && toggleNextLeaves(index)">

                    <label class="form-check-label ms-2" :for="'article-' + item.id">
                      <strong v-if="item.isSection">{{ item.title }}</strong>
                      <span v-else>
                        {{ item.versions?.length
                          ? item.versions[item.versions.length - 1].title
                          : item.title }}
                      </span>
                    </label>

                  </template>

                  <template v-else>
                    <strong class="ms-2 text-muted">{{ item.title }}</strong>
                  </template>

                </li>
              </template>
              <template v-else>
                <li class="text-muted">Caricamento in corso...</li>
              </template>

            </ul>

          </div>
          <div class="tag-list-articles">
            <div class="tag-list-articles-wrapper" ref="articlesWrapper">
              <div :class="['tag-list-articles', { 'collapsed': !expanded }]" ref="articlesList">
                <div v-for="(row, rowIndex) in groupedArticles" :key="rowIndex" class="tag-row-wrapper">
                  <div v-for="art in row" :key="art.id" class="tag-row">
                    <span class="tag-box" :title="getArticleDisplayTitleTooltip(art)"><strong>{{
                      getArticleDisplayTitle(art)
                        }}</strong></span>
                    <button v-if="canEditArticlesAndTopics || isExpertEditing" class="remove-btn"
                      @click="removeArticle(articlesSnapshot.indexOf(art))">×</button>
                  </div>
                </div>
              </div>


            </div>

          </div>
          <button v-if="showToggle" class="toggle-btn" @click="expanded = !expanded">
            <i :class="expanded ? 'bi bi-chevron-up' : 'bi bi-chevron-down'"></i>
          </button>
        </div>

        <!-- Argomenti correlati -->
        <div class="d-flex align-items-center gap-2 flex-wrap justify-content-between">
          <div class="argomenti-container">
            <div class="text-muted small mb-1 d-flex align-items-center gap-2">

              <strong>Argomenti correlati:</strong>

              <div class="dropdown" v-if="canModifyArticlesAndTopics">

                <button class="btn btn-primary small-rect-btn" data-bs-toggle="dropdown">+</button>

                <ul class="dropdown-menu p-2" style="max-height:250px; overflow-y:auto; min-width:250px;">
                  <li class="mb-2 px-2">
                    <input type="text" class="form-control form-control-sm" placeholder="Cerca..."
                      v-model="topicSearch">
                  </li>

                  <li v-for="topic in filteredTopics" :key="topic.id" class="form-check">
                    <input class="form-check-input" type="checkbox" :id="'topic-' + topic.id" :value="topic.id"
                      v-model="selectedTopicsIds">
                    <label class="form-check-label ms-2" :for="'topic-' + topic.id">{{ topic.title }}</label>
                  </li>
                </ul>
              </div>

              <div class="tag-list">
                <div v-for="(row, rowIndex) in groupedTopics" :key="rowIndex" class="tag-row-wrapper">
                  <div v-for="topic in row" :key="topic.id" class="tag-row">
                    <span class="tag-box"><strong>{{ topic.title }}</strong></span>
                    <button v-if="canModifyArticlesAndTopics" class="remove-btn"
                      @click="removeTopic(topicsSnapshot.indexOf(topic))">×</button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="question.state_id === 'ASSEVERATO' && formatAsseverazioneState(question)"
            class="asseverazione-wrapper d-flex align-items-center gap-2">
            <button v-if="canManageUsers && !showThreadPanel" class="btn btn-outline-secondary btn-sm rounded-pill px-2"
              @click="openHistoryModal" title="Cronologia quesito">
              <i class="bi bi-clock-history"></i>
            </button>
            <!-- Badge label -->
            <span class="badge status-label status-green">
              {{ formatAsseverazioneState(question).label }}
            </span>

            <!-- Cerchio pubblicazione -->
            <span class="publish-indicator"
              :class="formatAsseverazioneState(question).published ? 'published' : 'not-published'"
              :title="formatAsseverazioneState(question).published ? 'Pubblicato' : 'Non pubblicato'">
              {{ formatAsseverazioneState(question).published ? '✓' : '✕' }}
            </span>
          </div>

          <div v-else class="d-flex align-items-center gap-2">
            <button v-if="canManageUsers && !showThreadPanel" class="btn btn-outline-secondary btn-sm rounded-pill px-2"
              @click="openHistoryModal" title="Cronologia quesito">
              <i class="bi bi-clock-history"></i>
            </button>
            <span class="badge status-label" :class="`status-${formatState(question.state_id).color}`">
              {{ formatState(question.state_id).label }}
              <template v-if="isStorico && legacyStateLabel"> ({{ legacyStateLabel }})</template>
            </span>
          </div>
        </div>
        <!-- Esperto Legale (riassegnazione per UOC Manager) -->
        <div v-if="canReassignFromDetail" class="text-muted small mb-1 d-flex align-items-center gap-2 flex-wrap">
          <strong>Esperto Legale:</strong>
          <select v-model="selectedReassignExpertId" class="form-select form-select-sm" style="width:auto;">
            <option :value="null" disabled>Seleziona esperto...</option>
            <option v-for="user in expertsList" :key="user.id" :value="user.id">
              {{ user.name }} {{ user.surname }}
            </option>
          </select>
          <button class="btn btn-primary btn-sm" :disabled="!isReassignChanged" @click="reassignExpertFromDetail">
            Riassegna
          </button>
        </div>
        <!-- Esperto Assegnatario -->
        <div v-if="isExternalUser || (canManageUsers && question.state_id === 'ASSEGNATO')"
          class="d-flex align-items-center gap-2 flex-wrap justify-content-between">
          <div class="text-muted small mb-1 d-flex align-items-center gap-2 flex-wrap">
            <strong>Esperto Assegnatario:</strong>
            {{ question.expert?.name }} {{ question.expert?.surname }}
            <!--<template v-if="canReassign">
              <select v-model="question.expert_id" @change="reassignExpert" class="form-select form-select-sm"
                style="width:auto;">
                <option v-for="user in expertsList" :key="user.id" :value="user.id">
                  {{ user.name }} {{ user.surname }}
                </option>
              </select>
            </template>

            <template v-else>
              {{ question.expert?.name }} {{ question.expert?.surname }}
            </template>-->
          </div>
        </div>
        <div v-if="showLastAdministrator" class="text-muted small mb-1 d-flex align-items-center gap-2 flex-wrap">
          <strong>Ultimo Assegnatario:</strong>
          {{ question.last_administrator.name }} {{ question.last_administrator.surname }}
        </div>
      </div>

      <!-- Corpo quesito -->
      <div v-if="showExpertBodyPanel" class="question-body d-flex flex-grow-1 gap-3">

        <!-- Quesito -->
        <div class="quesito-panel d-flex flex-column gap-3 no-grow" style="flex: 1; min-height: 200px;">

          <div class="toggle-3d mb-3">
            <input type="checkbox" id="quesitoToggle" class="toggle-switch" v-model="quesitoView" true-value="editable"
              false-value="original">
            <label for="quesitoToggle" class="toggle-label">
              <span class="toggle-option toggle-left">Quesito originale</span>
              <span class="toggle-option toggle-right">Revisione Quesito</span>
            </label>
          </div>

          <div v-if="quesitoView === 'original'" class="p-2 border rounded original-box">
            {{ question.originalText }}
          </div>

          <div v-if="quesitoView === 'original'" class="small text-muted">{{ question.created_by?.organization + ' ' +
            formatDate(question.created_at) }}</div>


          <div v-else class="d-flex flex-column border p-2 rounded no-grow"
            style="flex:1; min-height:200px; overflow-y:auto;">
            <TiptapEditor :key="question.id" ref="editableEditor" v-model="editableQuestion" class="tiptap-wrapper"
              :editable="canEdit" :state-id="question.state_id" />
          </div>

        </div>

        <!-- Risposta -->
        <div v-if="isAssigned && !isPublishedAsseverato"
          class="d-flex flex-column border p-2 rounded no-grow risposta-panel"
          style="flex: 2 1 0%; overflow-y: auto; min-width:0;">
          <div class="fw-bold mb-2">Risposta</div>
          <TiptapEditor :key="question.id" ref="answerEditor" v-model="answerText" class="tiptap-wrapper"
            :editable="canEdit" :state-id="question.state_id" />
        </div>
        <div v-else>
          <div class="risposta-panel d-flex flex-column gap-3 no-grow" style="flex: 1; min-height: 200px;">

            <div v-if="isPublishedAsseverato" class="toggle-3d mb-3">
              <input type="checkbox" id="answerToggle" class="toggle-switch" v-model="answerView" true-value="opinion"
                false-value="answer">
              <label for="answerToggle" class="toggle-label">
                <span class="toggle-option toggle-left">Risposta</span>
                <span class="toggle-option toggle-right">Parere</span>
              </label>
            </div>

            <!-- RISPOSTA -->
            <div v-if="!isPublishedAsseverato || answerView === 'answer'" class="p-2 border rounded original-box"
              v-html="sanitize(question.answer)">
            </div>

            <!-- PARERE -->
            <div v-if="isPublishedAsseverato && answerView === 'opinion'" class="p-2 border rounded original-box"
              v-html="sanitize(question.opinion)">
            </div>

          </div>
        </div>

      </div>

      <!-- Corpo quesito -->
      <div v-if="showUOCReviewPanel" class="question-body d-flex flex-grow-1 gap-3">

        <!-- Quesito -->
        <div class="quesito-panel d-flex flex-column gap-3 no-grow" style="flex: 1; min-height: 200px;">

          <div class="toggle-3d mb-3">
            <input type="checkbox" id="quesitoToggle" class="toggle-switch" v-model="quesitoView" true-value="editable"
              false-value="original">
            <label for="quesitoToggle" class="toggle-label">
              <span class="toggle-option toggle-left">Quesito</span>
              <span class="toggle-option toggle-right">Risposta</span>
            </label>
          </div>

          <!--<div v-if="quesitoView === 'original'" class="p-2 border rounded original-box">
            {{ question.originalText }}
          </div>-->
          <div v-if="quesitoView === 'original'" class="p-2 border rounded original-box">
            <TiptapEditor v-if="canEditOriginalText" :key="'orig-' + question.id" ref="originalTextEditor"
              v-model="originalTextEditable" class="tiptap-wrapper" :editable="true" :state-id="question.state_id" />
            <div v-else class="p-2 rounded original-box" v-html="sanitize(question.originalText)">
            </div>
          </div>

          <div v-else class="p-2 border rounded original-box" v-html="sanitize(question.answer)">

          </div>
          <div v-if="quesitoView === 'original'" class="small text-muted">{{ question.created_by?.organization + ' ' +
            formatDate(question.created_at) }}</div>
          <!--<div v-if="quesitoView !== 'original'" class="small text-muted">
            {{ (question.sending_user?.name ?? '') + ' ' +
            (question.sending_user?.surname ?? '') + ' ' + formatDate(question.sending_date) }}
          </div>-->

        </div>

        <!-- Risposta -->
        <div v-if="isUOCManager" class="d-flex flex-column border p-2 rounded no-grow risposta-panel"
          style="flex: 2 1 0%; overflow-y: auto; min-width:0;">
          <div class="fw-bold mb-2">Parere</div>
          <TiptapEditor :key="question.id" ref="opinionEditor" v-model="opinionText" class="tiptap-wrapper"
            :editable="canEditOpinion" :state-id="question.state_id" />
          <!--
          
          Tolto riferimento all'esperto che ha scritto la risposta
          
          <div class="small text-muted">{{ (question.decided_by?.name ?? '') + ' ' + (question.decided_by?.surname ??
            '') + ' ' + formatDate(question.decided_on) }}</div>-->
        </div>
      </div>
      <div v-if="showThreadPanel" class="quesito-thread-panel d-flex flex-column gap-3 no-grow"
        style="flex: 1; min-height: 200px;">
        <!-- Editor per commentare -->
        <div v-if="showCommentEditor" class="border rounded p-2 mb-2 risposta-panel">
          <TiptapEditor v-model="commentDraft" :editable="true" class="tiptap-wrapper" />
          <div class="mt-2 text-end">
            <button class="btn btn-secondary btn-sm me-2" @click="cancelCommentEdit">Annulla</button>
            <button class="btn btn-primary btn-sm" @click="submitComment">{{ isIntegrationMode ? 'Integra' : 'Commenta' }}</button>
          </div>
        </div>

        <div class="d-flex justify-content-end mb-2 gap-2">
          <button v-if="canManageUsers" class="btn btn-outline-secondary btn-sm rounded-pill px-3"
            @click="openHistoryModal" title="Cronologia quesito">
            <i class="bi bi-clock-history"></i>
          </button>
          <button class="btn btn-primary btn-sm rounded-pill px-3" @click="downloadThreadPdf">
            📄 Esporta PDF
          </button>
        </div>

        <div class="thread-box d-flex flex-column gap-3" style="overflow-y:auto; flex:1; min-height:200px;">
          <div v-for="item in filteredThread" :key="item._uid" :class="[
            'thread-item',
            'mb-3',
            {
              'comment-yellow': item.type === 'comment' && item.type_id === 'C',
              'comment-integration': item.type === 'comment' && item.type_id === 'I',
              'thread-item-original': item.type === 'original',
              'thread-item-answer-draft': item.type === 'answer-draft',
              'thread-item-revision': item.type === 'revision',
              'thread-item-answer': item.type === 'answer',
              'thread-item-opinion': item.type === 'opinion',
              'border rounded p-2': !['comment', 'original', 'revision', 'answer', 'opinion'].includes(item.type)
            }
          ]">

            <!-- Label -->
            <span v-if="['original', 'revision', 'answer', 'answer-draft', 'opinion'].includes(item.type)"
              class="thread-label" :class="{
                'label-original': item.type === 'original' || item.type === 'answer-draft',
                'label-revision': item.type === 'revision',
                'label-answer': item.type === 'answer',
                'label-opinion': item.type === 'opinion'
              }">
              {{
                item.type === 'original'
                  ? 'Quesito originale'
                  : item.type === 'revision'
                    ? 'Revisione'
                    : item.type === 'answer'
                      ? 'Risposta'
                      : item.type === 'opinion'
                        ? 'Parere'
                        : 'Bozza Risposta'
              }}
            </span>

            <!-- Header -->
            <div class="thread-header d-flex align-items-center mb-2 thread-header-common">
              <div class="avatar me-2">
                <span v-if="item.type === 'original'">{{ item.user?.initials }}</span>
                <span v-else>{{ getInitials(item.user) }}</span>
              </div>
              <div class="user-info me-auto">
                <div class="user-name">{{ item.user?.name }} {{ item.user?.surname }}</div>
                <div class="comment-date">{{ formatDate(item.date) }}</div>
              </div>
            </div>

            <!-- Contenuto -->
            <div class="thread-content" v-html="sanitize(item.html || item.text)"></div>

            <!-- Bottoni modifica/elimina -->
            <div v-if="item.type === 'comment' && item.user?.id === $root.user?.id"
              class="mt-2 d-flex justify-content-end gap-2">
              <button class="btn btn-sm btn-primary" style="padding: 0.25rem 0.5rem; font-size: 0.75rem;"
                :disabled="isEditDeleteDisabled" @click="startEditComment(item)">
                Modifica
              </button>
              <button class="btn btn-sm btn-danger" style="padding: 0.25rem 0.5rem; font-size: 0.75rem;"
                :disabled="isEditDeleteDisabled" @click="confirmDeleteComment(item)">
                Elimina
              </button>
            </div>

            <!-- SimpleModal per conferma eliminazione -->
            <SimpleModal v-model="showDeleteModal" title="Sei sicuro di eliminare il tuo commento?"
              :buttons="['Si, elimino', 'No, annulla']" @chose="handleDeleteChoice" />


          </div>
        </div>
      </div>
      <!-- Modale Cronologia -->
      <!-- Modale Cronologia -->
      <ModalView v-model="showHistoryModal" :title="`Cronologia Quesito #${question.id}`" icon="clock-history">
        <div v-if="isLoadingHistory" class="text-center py-4">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Caricamento...</span>
          </div>
        </div>
        <div v-else-if="historyItems.length === 0" class="text-center text-muted py-4">
          Nessuna cronologia disponibile.
        </div>
        <div v-else>
          <table class="table table-striped table-hover table-sm align-middle text-center">
            <thead class="table-light">
              <tr>
                <th style="width:33.33%">Data/Ora</th>
                <th style="width:33.33%">Stato</th>
                <th style="width:33.33%">Utente</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in historyItems" :key="item.id">
                <td class="text-nowrap">{{ formatDateTime(item.created_at) }}</td>
                <td>
                  <span class="badge" :class="getStateBadgeClass(item.state_id)">
                    {{ getStateLabel(item.state_id) }}
                  </span>
                </td>
                <td>{{ item.modified_user_name }} {{ item.modified_user_surname || '' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </ModalView>
    </div>
  </div>
</template>

<script>
import { sanitize } from '../../../utils/sanitize';
import ToastDialog from '../../../components/common/ToastDialog.vue';
import { QuillEditor } from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';
import toastable from '../../../mixins/toastable';
import TiptapEditor from '../../../components/common/TiptapEditor.vue';
import ArticlesView from '../ArticlesView.vue';
import ArticleView from '../ArticleView.vue'
import ModalView from '../../../components/common/ModalView.vue';
import SimpleModal from '../../../components/common/SimpleModal.vue';
import { STATE_CONFIG } from '../../../utils/stateConfig';
import navigationHistory from '../../../services/navigationHistory';

export default {
  name: 'QuestionDetailExpertView',
  components: { ToastDialog, QuillEditor, TiptapEditor, ArticlesView, ArticleView, ModalView, SimpleModal },
  mixins: [toastable],
  props: ['elementId', 'articleId', 'version'],
  data() {
    return {
      isLoading: true,
      isChangingState: false,
      question: {
        object: '',
        text: '',
        topics: [],
        articles: [],
        legislation_id: '',
        answer: '',
      },
      legislations: [],
      availableTopics: [],
      availableArticles: [],
      topicSearch: '',
      articleSearch: '',
      answerText: '',
      topicsSnapshot: [],
      articlesSnapshot: [],
      revisionText: null,
      elements: [],
      leftView: 'articles',
      leftPanelVisible: true,
      showArticleModal: false,
      modalArticleId: null,
      modalVersion: null,
      comments: [],
      selectedTopicsIds: [],
      selectedArticlesIds: [],
      modalCounter: 0,
      localElementId: this.elementId,
      editableQuestion: '',
      quesitoView: 'original',
      answerView: 'answer',
      showConfirmModal: false,
      isAnswered: false,
      showSuccessModal: false,
      showCommentEditor: false,
      isIntegrationMode: false,
      showDeleteModal: false,
      commentToDelete: null,
      expertsList: [],
      opinionText: '',
      response: '',
      showUOCDecisionModal: false,
      pendingUOCDecision: null,
      showMoveModal: false,
      moveDirection: null,
      stableQuestionId: null,
      mustSaveArticlesAndTopics: false,
      showForwardModal: false,
      isDraftSaved: false,
      isRevisingAsseveration: false,
      expanded: false,
      showToggle: false,
      selectedReassignExpertId: null,
      originalTextEditable: '',
      showHistoryModal: false,
      historyItems: [],
      isLoadingHistory: false,
    };
  },
  computed: {
    isInUocReviewState() {
      return ['IN_ESAMINA_UOC', 'ASSEVERATO', 'NON_ASSEVERATO'].includes(this.question.state_id);
    },
    isInFinalState() {
      return ['ASSEGNATO', 'EVASO', 'ASSEVERATO', 'IN_ESAMINA_UOC', 'NON_ASSEVERATO'].includes(this.question.state_id);
    },
    isStorico() {
      return this.question.state_id === 'STORICO';
    },
    legacyStateLabel() {
      if (!this.isStorico) return '';
      const map = {
        'AssignedToUffLeg': 'Assegnato a Ufficio Legale',
        'TakenByUffLeg': 'Preso in carico da Ufficio Legale',
        'AnsweredByUffLeg': 'Evaso da Ufficio Legale',
        'RejectedByUffLeg': 'Rifiutato da Ufficio Legale',
        'RejectedByExpert': 'Rifiutato da Esperto',
        'RatedForUOC': 'Valutato per UOC',
        'AssignedToUOC': 'Assegnato a UOC',
        'AssignedToPublisher': 'Assegnato a publisher',
        'TakenByPublisher': 'Preso in carico da publisher',
        'LostAndFound': 'Lost and found',
      };
      return map[this.question.legacy_state] || this.question.legacy_state || '';
    },
    isMyQuestion() {
      return this.question.expert_id === this.$root.user?.id;
    },
    isMyQuestionNotMoving() {
      return this.isMyQuestion && !this.canShowMoveToUOC && !this.canShowMoveToWaiting;
    },
    showLeftPanelToggle() {
      return (this.isAssigned && !this.isUOCManager) ||
        (this.isUOCManager && this.isAssignedUoc && this.isInUocReviewState)||+
        (this.isUOCManager && this.question.state_id === 'IN_ESAMINA_UOC');
    },
    showAssignedActions() {
      if (this.isStorico) return false;
      return this.isAssigned && (!this.isUOCManager || !this.isInUocReviewState);
    },
    canIntegrateInAssigned() {
      return this.canShowIntegration &&
        !['ASSEGNATO', 'IN_LAVORAZIONE', 'INVIATO'].includes(this.question.state_id);
    },
    canModifyArticlesAndTopics() {
      return this.isExpertEditing || this.canEditArticlesAndTopics;
    },
    showAddArticleDropdown() {
      return this.canModifyArticlesAndTopics && this.question.legislation_id !== null;
    },
    showExpertBodyPanel() {
      if (this.isStorico) return false;
      return this.isAssigned &&
        (!this.isUOCManager || ['ASSEGNATO', 'IN_LAVORAZIONE'].includes(this.question.state_id));
      /** da aggiungere nel caso limite in cui io super user voglio riassegnare un quesito a cui io ho risposto*/
      /**if (this.question.state_id === 'ASSEGNATO' && !!this.question.opinion && this.isAssignedUoc) return false;
      return this.isAssigned &&
        (!this.isUOCManager || ['ASSEGNATO', 'IN_LAVORAZIONE'].includes(this.question.state_id));*/
    },
    showUOCReviewPanel() {
      const s = this.question.state_id;
      return (this.isUOCManager && s === 'IN_ESAMINA_UOC') ||
        (['ASSEVERATO', 'NON_ASSEVERATO'].includes(s) && this.isAssignedUoc) ||
        this.isRevisingAsseveration;
    },
    showThreadPanel() {
      const s = this.question.state_id;
      //if (s === 'ASSEGNATO' && !!this.question.opinion && this.isAssignedUoc) return false;
      if (!this.isAssigned && !this.isUOCManager) return true;
      if (this.isAssigned && this.isUOCManager && s === 'EVASO') return true;
      if (this.isUOCManager && !this.isInUocReviewState && !this.isAssigned) return true;
      if (this.isUOCManager && !this.isAssignedUoc && !this.isRevisingAsseveration &&
        ['ASSEVERATO', 'NON_ASSEVERATO'].includes(s)) return true;
      return false;
    },
    showLastAdministrator() {
      return this.question.last_administrator && this.isMyQuestion && !this.canReply;
    },
    isIntegrateDisabledInAssigned() {
      return (this.isUOCMember && ['ASSEVERATO', 'NON_ASSEVERATO'].includes(this.question.state_id)) ||
        (this.isUOCManager && this.isInUocReviewState);
    },
    disableSaveDraftBtn() {
      return this.isInFinalState || this.isAnswered;
    },
    disableReplyBtn() {
      return this.isInFinalState || this.isAnswered || !this.answerText;
    },
    disableForwardBtn() {
      return this.isInFinalState;
    },
    isCommentDisabled() {
      return this.isInUocReviewState;
    },
    isIntegrateDisabledInElse() {
      return ['ASSEVERATO', 'NON_ASSEVERATO'].includes(this.question.state_id) ||
        (this.isUOCManager && ['ASSEVERATO', 'NON_ASSEVERATO', 'IN_ESAMINA_UOC', 'ASSEGNATO', 'INVIATO'].includes(this.question.state_id));
    },
    canReassignFromDetail() {
      return this.isUOCManager &&
        this.canAssignQuestion &&
        ['EVASO'].includes(this.question.state_id) && this.question.is_in_sala_uoc == false;
    },
    isReassignChanged() {
      return true;
      /**return this.selectedReassignExpertId != null &&
        this.selectedReassignExpertId !== this.question.expert_id;*/
    },
    canManageUsers() {
      return this.$root.user?.permissions?.includes('manage-users')
    },
    canAssignQuestion() {
      return this.$root.user?.permissions?.includes('assign-question')
    },
    commentLabel() {
      const permissions = this.$root.user?.permissions || [];

      const canComment = permissions.includes('add-comments');
      const canIntegration = permissions.includes('add-integration');

      if (canComment && canIntegration) {
        return 'Int/Comm';
      }
      return this.isUOCManager ? 'Integrazioni' : 'Commenti';
    },
    canEditComment() { return this.$root.user?.permissions?.includes('add-comment'); },
    isExpertEditing() {
      return this.question.expert_id === this.$root.user?.id &&
        ['RISPOSTA_IN_BOZZA', 'IN_LAVORAZIONE'].includes(this.question.state_id);
    },
    canRiesamina() {
      return (
        this.isUOCManager &&
        ['ASSEVERATO', 'NON_ASSEVERATO'].includes(this.question.state_id) &&
        !this.isRevisingAsseveration
      );
    },
    async exportThreadPdf() {
      try {
        const response = await this.$root.$api.get(
          `/user/pdf-download-thread/${this.question.id}`,
          {
            responseType: 'blob'
          }
        );

        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Thread_Quesito_${this.question.id}.pdf`);
        document.body.appendChild(link);
        link.click();

        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);

      } catch (error) {
        console.error('Errore durante l\'esportazione del PDF:', error);
        this.$toast?.error?.('Errore durante l\'esportazione del PDF');
      }
    },
    isPublishedAsseverato() {
      return this.question.state_id === 'ASSEVERATO' && this.question.published === true;
    },
    canReply() {
      return this.$root.user?.permissions?.includes('reply-to-question');
    },
    isEditDeleteDisabled() {
      const perms = this.$root.user?.permissions || [];

      if (perms.includes('add-comments') &&
        ['ASSEVERATO', 'NON_ASSEVERATO', 'IN_ESAMINA_UOC'].includes(this.question.state_id)) {
        return true;
      }

      if (perms.includes('add-integration') &&
        ['ASSEVERATO', 'NON_ASSEVERATO'].includes(this.question.state_id)) {
        return true;
      }

      return false;
    },
    isInEsaminaUOC() {
      return [
        'IN_ESAMINA_UOC',
        'ASSEVERATO',
        'NON_ASSEVERATO'
      ].includes(this.question?.state_id);
    },
    backView() {
      if (!this.question) return 'SalaAttesaSalaUOCView';

      return this.question.state_id === 'EVASO'
        ? 'SalaAttesaSalaUOCView'
        : 'UOCArchiveView';

      //@click.prevent="$emit('change-view', 'SessioneUOCView', item.id)"> la question e associata a un id uoc. quindi
      // se è associata una uoc torno indietro e richiamo quello li
    },
    canShowMoveToUOC() {
      return (
        this.isUOCManager &&
        //!this.isAssigned &&
        this.question.state_id === 'EVASO' &&
        !this.question.is_in_sala_uoc

      );
    },

    canShowMoveToWaiting() {
      return this.isUOCManager &&
        this.question.state_id === 'EVASO' &&
        this.question.is_in_sala_uoc;
    },

    isExternalUser() {
      return !this.isUOCManager && !this.isUOCMember;
    },

    canShowUOCDecisionDropdown() {
      return (
        this.isUOCManager &&
        //!this.isAssigned &&
        (
          this.question.state_id === 'IN_ESAMINA_UOC' ||
          this.isRevisingAsseveration
        )
      );
    },

    canShowComment() {
      if (this.isStorico) return false;
      return (
        (this.question.state_id !== 'IN_ESAMINA_UOC' && this.question.state_id !== 'ASSEVERATO' && this.question.state_id !== 'NON_ASSEVERATO') &&
        ((!this.isAssigned && this.isExternalUser) ||
          (!this.isAssigned && this.$root.user?.permissions?.includes('add-comments')))
      );
    },

    canShowIntegration() {
      if (this.isStorico) return false;
      return (
        //!this.isAssigned &&
        (this.isUOCMember && this.$root.user?.permissions?.includes('add-integration')) || this.$root.user?.permissions?.includes('add-integration')
      );
    },


    isSaveDisabled() {
      if (this.isUOCManager) {
        return (
          ['ASSEVERATO', 'NON_ASSEVERATO'].includes(this.question.state_id) &&
          !this.isRevisingAsseveration
        );
      }
      return (
        this.question.state_id === 'ASSEGNATO' ||
        this.question.state_id === 'EVASO' ||
        this.isAnswered
      );
    },
    isUOCMember() {
      const perms = this.$root.user?.permissions || [];
      return perms.includes('view-uoc') && !perms.includes('manage-uoc');
    },

    isUOCManager() {
      return this.$root.user?.permissions?.includes('manage-uoc');
    },

    isOpinionEmpty() {
      const text = this.opinionText?.trim() ?? '';
      return !text || text === '<p></p>';
    },

    canViewUOC() {
      return this.isUOCMember || this.isUOCManager;
    },
    canManageAndViewUOC() {
      return this.$root.user?.permissions?.includes('view-uoc') &&
        this.$root.user?.permissions?.includes('manage-uoc');
    },
    canReassign() {
      const allowedStates = ['IN_LAVORAZIONE', 'ASSEGNATO', 'INVIATO'];
      return this.$root.user?.permissions?.includes('assign-question') &&
        allowedStates.includes(this.question.state_id);
    },
    filteredThread() {
      const allowedStates = [
        'EVASO',
        'IN_SALA_ATTESA',
        'IN_ESAMINA_UOC',
        'NON_ASSEVERATO',
        'ASSEVERATO',
        'STORICO'
      ];

      if (!this.canReply || (this.question.state_id === 'ASSEGNATO' && !!this.question.answer)) {
        allowedStates.push('ASSEGNATO');
      }

      const typePriority = {
        opinion: 4,
        answer: 3,
        'answer-draft': 3,
        revision: 2,
        comment: 1,
        original: 0
      };

      return this.combinedThread
        .filter(item => {
          if (!item) return false;
          if (item.type === 'original' || item.type === 'comment') return true;
          return allowedStates.includes(this.question.state_id);
        })
        .sort((a, b) => {
          const dateA = new Date(a.date || a.created_at).getTime();
          const dateB = new Date(b.date || b.created_at).getTime();

          if (dateA !== dateB) {
            return dateB - dateA;
          }

          const prioA = typePriority[a.type] ?? 0;
          const prioB = typePriority[b.type] ?? 0;

          return prioB - prioA;
        });
    },
    canComment() {
      //return (this.question?.state_id === 'IN_LAVORAZIONE' || this.question?.state_id === 'EVASO' || this.question?.state_id === 'RISPOSTA_IN_BOZZA');
      if (this.isUOCManager) return false;
      const normalComment = ['IN_LAVORAZIONE', 'EVASO', 'RISPOSTA_IN_BOZZA'].includes(this.question?.state_id);
      const gestoreComment = this.isGestore && this.question?.state_id === 'ASSEGNATO';
      return normalComment || gestoreComment;
    },
    combinedThread() {
      const items = [];

      // Quesito originale
      items.push({
        _uid: 'q-original',
        type: 'original',
        label: 'Quesito originale',
        text: this.question.originalText,
        date: this.question.created_at,
        user: {
          name: this.question.created_by?.organization,
          surname: '',
          initials: this.getInitialsFromOrg(this.question.created_by?.organization)
        }
      });

      // Revisione quesito
      /**if (this.question.text !== this.question.originalText) {
        items.push({
          _uid: 'q-revision',
          type: 'revision',
          label: 'Revisione quesito',
          text: this.question.text,
          date: this.question.sending_date ?? this.question.forward_on,
          user: {
           name: this.question.sending_user?.name ?? this.question.forward_user?.name,
           surname: this.question.sending_user?.surname ?? this.question.forward_user?.surname,
          }
        });
      }*/

      if (this.question.answer && !this.question.sending_date) {
        const currentUserId = this.$root.user?.id;
        if (this.question.forward_id === currentUserId) {
          items.push({
            _uid: 'answer-draft',
            type: 'answer-draft',
            label: 'Bozza Risposta',
            text: this.question.answer,
            date: this.question.forward_on,
            user: {
              name: this.question.forward_user?.name,
              surname: this.question.forward_user?.surname,
            }
          });
        }
      }

      // Risposta
      if (this.question.answer && this.question.sending_date) {
        items.push({
          _uid: 'answer',
          type: 'answer',
          label: 'Risposta',
          text: this.question.answer,
          date: this.question.sending_date,
          user: {
            name: (this.isUOCMember || this.isUOCManager) ? 'Utente' : this.question.sending_user?.name,
            surname: (this.isUOCMember || this.isUOCManager) ? 'Esperto' : this.question.sending_user?.surname,
          }
        });
      }

      // Parere
      if (this.question.opinion) {
        items.push({
          _uid: 'opinion',
          type: 'opinion',
          label: 'Parere',
          text: this.question.opinion,
          date: this.question.decided_on,
          user: {
            name: this.question.decided_by?.name,
            surname: this.question.decided_by?.surname,
          }
        });
      }

      // Commenti
      this.comments.forEach(c => {
        items.push({
          ...c,
          type: 'comment',
          label: 'Commento',
          subLabel: c.user ? `${c.user.name} ${c.user.surname}` : '',
          date: c.created_at,
          _uid: `comment-${c.id}`
        });
      });

      return items.sort((a, b) =>
        new Date(b.date || b.created_at) - new Date(a.date || a.created_at)
      );
    },
    isAssigned() {
      return this.question.expert_id === this.$root.user?.id;
    },
    isAssignedUoc() {
      return this.question.decided_by?.id === this.$root.user?.id;
    },
    canEdit() {
      return this.question.expert_id === this.$root.user?.id &&
        (this.question.state_id === 'IN_LAVORAZIONE' || this.question.state_id === 'RISPOSTA_IN_BOZZA');
    },
    canEditOpinion() {
      return (
        this.isUOCManager &&
        (
          this.question.state_id === 'IN_ESAMINA_UOC' ||
          this.isRevisingAsseveration
        )
      );
    },
    canEditOriginalText() {
      return (
        (this.isUOCManager && !this.canManageUsers) &&
        (['IN_ESAMINA_UOC'].includes(this.question.state_id) || this.isRevisingAsseveration)
      );
    },
    async prendiInCarico() {
      this.isChangingState = true;
      const today = new Date();
      const expiresOn = new Date(today);
      expiresOn.setDate(today.getDate() + 15);

      const payload = {
        id: this.question.id,
        state_id: 'IN_LAVORAZIONE',
        //expires_on: expiresOn.toISOString().split('T')[0]
      };

      try {
        const response = await this.$root.$api.put(`/questions/${this.question.id}`, payload);

        this.question.state_id = response.data.question.state_id;
        //this.question.expires_on = response.data.question.expires_on;
        this.question.updated_at = response.data.question.updated_at;

        this.showSuccessToast("Successo", "Quesito preso in carico");
      } catch (err) {
        console.error(err);
        this.showErrorToast("Errore", "Impossibile prendere in carico il quesito");
      } finally {
        this.isChangingState = false;
      }
    },
    canTakeOver() {
      return this.question.expert_id != null &&
        this.question.expert_id === this.$root.user?.id &&
        this.question.state_id === 'ASSEGNATO';
    },
    filteredArticles() {
      let articles = this.availableArticles.slice(); // copia per non modificare originale

      if (this.articleSearch) {
        articles = articles
          .filter(a => !a.isSection)
          .filter(a => {
            const title =
              a.versions?.length
                ? a.versions[a.versions.length - 1].title
                : a.title ?? '';

            return title
              .toLowerCase()
              .includes(this.articleSearch.toLowerCase());
          });
      }

      // ordino per id o per titolo
      return articles.sort((a, b) => a.id - b.id);
    }
    ,
    filteredTopics() {
      if (!this.topicSearch) return this.availableTopics;
      return this.availableTopics.filter(t => t.title.toLowerCase().includes(this.topicSearch.toLowerCase()));
    },
    groupedTopics() {
      const chunkSize = 4;
      const result = [];
      for (let i = 0; i < this.topicsSnapshot.length; i += chunkSize) {
        result.push(this.topicsSnapshot.slice(i, i + chunkSize));
      }
      return result;
    },
    groupedArticles() {
      const chunkSize = 4;
      const result = [];
      for (let i = 0; i < this.articlesSnapshot.length; i += chunkSize) {
        result.push(this.articlesSnapshot.slice(i, i + chunkSize));
      }
      return result;
    },
    isGestore() {
      return this.$root.user?.permissions?.includes('assign-question');
    },
    canEditArticlesAndTopics() {
      if (this.isStorico) return false;
      //return ((this.isGestore && this.question?.state_id === 'INVIATO') || (this.isUOCManager && this.question?.state_id !== 'ASSEVERATO' && this.question?.state_id !== 'NON_ASSEVERATO'));
      return (
        (this.isGestore && this.question?.state_id === 'INVIATO') ||
        (
          this.isUOCManager &&
          (
            !['ASSEGNATO', 'ASSEVERATO', 'NON_ASSEVERATO'].includes(this.question?.state_id)
            || this.isRevisingAsseveration
          )
        )
      );
    }

  },
  watch: {
    elementId: {
      immediate: true,
      handler(newId, oldId) {
        if (newId === "0") return;
        if (newId && oldId && newId !== oldId) {
          this.openModalArticle();
        }
      }
    },
    version(newVersion, oldVersion) {
      if (newVersion) {
        //this.reloadQuestion();
      }
    },
    question: {
      deep: true,
      immediate: true,
      handler(newQ) {
        this.editableQuestion = newQ.text;
        if (this.isUOCManager) {
          this.opinionText = newQ.opinion || '';
        } else {
          this.answerText = newQ.answer || '';
          this.opinionText = '';
        }
      }
    },

    selectedArticlesIds(newIds) {
      if (this.availableArticles.length === 0) return;
      this.articlesSnapshot = this.availableArticles.filter(a => newIds.includes(a.id));
      console.log('selectedArticlesIds changed:', newIds);
      this.$nextTick(() => {
        this.checkArticlesHeight();
      });
    },
    selectedTopicsIds(newIds) {
      this.topicsSnapshot = this.availableTopics.filter(t => newIds.includes(t.id));
    },
    availableArticles(newArticles) {
      if (!this.selectedArticlesIds?.length) return;
      this.articlesSnapshot = newArticles.filter(a => this.selectedArticlesIds.includes(a.id));
    }
  },
  methods: {
    sanitize(html) {
      return sanitize(html);
    },
    async waitForRootContents(timeoutMs = 10000) {
      const start = Date.now();

      while (
        (!this.$root.contents?.articles || this.$root.contents.articles.length === 0) &&
        (!this.$root.contents?.sections || this.$root.contents.sections.length === 0)
      ) {
        if (Date.now() - start > timeoutMs) {
          break;
        }
        await new Promise(r => setTimeout(r, 50));
      }
    },
    async openHistoryModal() {
      this.showHistoryModal = true;
      this.isLoadingHistory = true;
      try {
        const response = await this.$root.$api.get(`/questions/${this.question.id}/history`);
        this.historyItems = response.data;
      } catch (err) {
        console.error(err);
        this.showErrorToast('Errore', 'Impossibile caricare la cronologia');
      } finally {
        this.isLoadingHistory = false;
      }
    },
    formatDateTime(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      return d.toLocaleDateString('it-IT') + ' ' + d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
    },
    getStateLabel(stateId) {
      const config = STATE_CONFIG[stateId];
      return config?.label || stateId || '—';
    },
    getStateBadgeClass(stateId) {
      const map = {
        INVIATO: 'bg-warning text-dark',
        ASSEGNATO: 'bg-primary',
        IN_LAVORAZIONE: 'bg-orange',
        RISPOSTA_IN_BOZZA: 'bg-secondary',
        EVASO: 'bg-success',
        IN_ESAMINA_UOC: 'bg-info text-dark',
        ASSEVERATO: 'bg-success',
        NON_ASSEVERATO: 'bg-danger',
        STORICO: 'bg-dark'
      };
      return map[stateId] || 'bg-secondary';
    },
    getTimelineDotClass(stateId) {
      const map = {
        INVIATO: 'dot-warning',
        ASSEGNATO: 'dot-primary',
        IN_LAVORAZIONE: 'dot-orange',
        EVASO: 'dot-success',
        IN_ESAMINA_UOC: 'dot-info',
        ASSEVERATO: 'dot-success',
        NON_ASSEVERATO: 'dot-danger',
        STORICO: 'dot-dark'
      };
      return map[stateId] || 'dot-secondary';
    },

    checkArticlesHeight() {
      const list = this.$refs.articlesList;
      if (list) {
        this.showToggle = list.scrollHeight > 32;
      }
    },
    toggleNextLeaves(startIndex) {

      const parent = this.filteredArticles[startIndex];

      let i = startIndex + 1;
      const idsToToggle = [];

      while (
        i < this.filteredArticles.length &&
        !this.filteredArticles[i].isSection
      ) {
        idsToToggle.push(this.filteredArticles[i].id);
        i++;
      }

      const allSelected = idsToToggle.every(id =>
        this.selectedArticlesIds.includes(id)
      );

      if (allSelected) {
        this.selectedArticlesIds = this.selectedArticlesIds
          .filter(id => !idsToToggle.includes(id));
      } else {
        this.selectedArticlesIds = [
          ...new Set([...this.selectedArticlesIds, ...idsToToggle])
        ];
      }

      this.selectedArticlesIds = this.selectedArticlesIds
        .filter(id => id !== parent.id);
    },
    showCheckbox(item, index) {

      if (!item.isSection) {
        return true;
      }

      const next = this.filteredArticles[index + 1];

      if (item.isSection && next && !next.isSection) {
        return true;
      }

      return false;
    },
    onSelectArticleOrSection(item) {
      const collectLeafIds = (node) => {
        if (!node.children || node.children.length === 0) {
          return node.isSection ? [] : [node.id];
        }
        return node.children.flatMap(c => collectLeafIds(c));
      };

      if (item.isSection) {
        const idsToToggle = collectLeafIds(item);
        if (!idsToToggle.length) return;

        const allSelected = idsToToggle.every(id => this.selectedArticlesIds.includes(id));

        if (allSelected) {
          this.selectedArticlesIds = this.selectedArticlesIds.filter(id => !idsToToggle.includes(id));
        } else {
          this.selectedArticlesIds = Array.from(new Set([...this.selectedArticlesIds, ...idsToToggle]));
        }
      } else {
        if (this.selectedArticlesIds.includes(item.id)) {
          this.selectedArticlesIds = this.selectedArticlesIds.filter(
            id => id !== item.id
          );
        } else {
          this.selectedArticlesIds.push(item.id);
        }
      }

      this.articlesSnapshot = this.availableArticles
        .filter(a => !a.isSection && this.selectedArticlesIds.includes(a.id));

      this.question.articles = [...this.articlesSnapshot];
    }



    ,
    extractAllArticlesAndSections() {
      const sections = this.$root.contents?.sections || [];
      const articles = this.$root.contents?.articles || [];

      const nodeMap = {};

      sections.forEach(s => {
        const latestVersion = s.version?.[s.version.length - 1];

        nodeMap[`section-${s.id}`] = {
          key: `section-${s.id}`,
          id: s.id,
          parent: s.parent ? `section-${s.parent}` : null,
          isSection: true,
          title: s.title,
          order: latestVersion?.pivot?.order ?? s.order ?? 0,
          children: []
        };
      });

      articles.forEach(a => {
        const latestVersion = a.versions?.[a.versions.length - 1];

        nodeMap[`article-${a.id}`] = {
          key: `article-${a.id}`,
          id: a.id,
          parent: a.section ? `section-${a.section}` : null,
          isSection: false,
          versions: a.versions,
          title: latestVersion?.title ?? '',
          order: latestVersion?.order ?? 0,
          children: []
        };
      });

      Object.values(nodeMap).forEach(node => {
        if (node.parent && nodeMap[node.parent]) {
          nodeMap[node.parent].children.push(node);
        }
      });

      const roots = Object.values(nodeMap).filter(
        n => n.isSection && !n.parent
      );

      const flatten = (node) => {
        let list = [];

        list.push({
          id: node.key,
          originalId: node.id,
          isSection: node.isSection,
          title: node.title,
          order: node.order,
          versions: node.versions ?? []
        });

        const sortedChildren = [...node.children].sort(
          (a, b) => (a.order ?? 0) - (b.order ?? 0)
        );

        sortedChildren.forEach(child => {
          list.push(...flatten(child));
        });

        return list;
      };

      let result = [];

      roots
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .forEach(root => {
          result.push(...flatten(root));
        });

      return result;
    }


    ,
    async saveDraftUoc() {
      if (!this.question?.id) return;

      this.isChangingState = true;
      await this.$nextTick();

      let realAnswer = '';
      let realEditableQuestion = '';

      if (this.question.state_id === 'IN_LAVORAZIONE') {
        realAnswer = this.$refs.answerEditor?.editor?.getHTML?.() || this.answerText;
        realEditableQuestion = this.$refs.editableEditor?.editor?.getHTML?.() || this.editableQuestion;
      }

      const parere =
        this.$refs.opinionEditor?.editor?.getHTML?.() || this.opinionText;

      const realOriginalText =
        this.$refs.originalTextEditor?.editor?.getHTML?.() || this.originalTextEditable;

      const payload = {
        id: this.question.id,
        topics: this.topicsSnapshot.map(t => t.id),
        //articles: this.articlesSnapshot.map(a => a.id),
        articles: this.articlesSnapshot
          .filter(a => !a.isSection)
          .map(a => a.originalId),
        opinion: parere,
        answer: realAnswer,
        editable_question: realEditableQuestion,
        original_text: realOriginalText,
        uoc_draft: true
      };

      try {
        const response = await this.$root.$api.put(`/questions/${this.question.id}`, payload);

        this.question.updated_at = response.data.question.updated_at;
        this.question.opinion = response.data.question.opinion;
        this.question.originalText = realOriginalText;

        this.showSuccessToast(
          'Salvataggio effettuato',
          'Modifiche UOC salvate in bozza'
        );
      } catch (err) {
        console.error(err);
        this.showErrorToast(
          'Errore',
          'Impossibile salvare la bozza UOC'
        );
      } finally {
        this.isChangingState = false;
      }
    },
    enableRiesamina() {
      this.isRevisingAsseveration = true;
      this.showSuccessToast(
        'Riesamina attiva',
        'Ora puoi modificare articoli, argomenti e parere'
      );
    },
    async downloadThreadPdf() {
      try {
        const response = await this.$root.$api.get(
          `/pdf-download-thread/${this.question.id}`,
          {
            responseType: 'blob'
          }
        );

        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Thread_Quesito_${this.question.id}.pdf`);
        document.body.appendChild(link);
        link.click();

        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);

      } catch (error) {
        console.error('Errore durante l\'esportazione del PDF:', error);
        this.$toast?.error?.('Errore durante l\'esportazione del PDF');
      }
    }
    ,
    async handleForward(choice) {
      if (choice !== 'Conferma') {
        return;
      }
      try {
        this.isLoading = true;
        await this.$nextTick();
        const realAnswer = this.$refs.answerEditor?.editor?.getHTML?.() || this.answerText;
        const realEditableQuestion = this.$refs.editableEditor?.editor?.getHTML?.() || this.editableQuestion;
        const payload = {
          id: this.question.id,
          answer: realAnswer,
          topics: this.topicsSnapshot.map(t => t.id),
          //articles: this.articlesSnapshot.map(a => a.id),
          articles: this.articlesSnapshot
            .filter(a => !a.isSection)
            .map(a => a.originalId),
        };
        await this.$root.$api.put(`/questions/${this.question.id}`, payload);
        await this.$root.$api.post(`/questions/${this.question.id}/forward`);
        //await this.reloadQuestion();
        this.question.expert_id = this.question.last_administrator.id;
        this.question.state_id = 'ASSEGNATO';
        this.question.answer = realAnswer;
        this.question.forward_id = this.$root.user.id;
        this.question.forward_user = {
          id: this.$root.user.id,
          name: this.$root.user.name,
          surname: this.$root.user.last_name
        };
        this.question.forward_on = new Date().toISOString();
        this.question.expert_id = this.question.last_administrator.id;
        this.question.expert.name = this.question.last_administrator.name;
        this.question.expert.surname = this.question.last_administrator.surname;
        this.showSuccessToast("Successo", "Quesito inoltrato correttamente");
      } catch (err) {
        console.error(err);
        this.showErrorToast("Errore", "Impossibile inoltrare il quesito");
      } finally {
        this.isLoading = false;
      }
    },
    handleBack() {

      if (navigationHistory.hasHistory()) {
        const previousView = navigationHistory.peek(); // USA PEEK invece di POP
        if (previousView) {
          // Naviga alla view precedente
          // NON fare pop qui, lo farà il mounted della view di destinazione
          this.$emit('change-view', previousView.viewName, previousView.elementId);
          return;
        }
      }

      if (
        this.question?.uoc_session_id &&
        this.isInEsaminaUOC
      ) {
        this.$emit(
          'change-view',
          'SessioneUOCView',
          this.question.uoc_session_id
        );
        return;
      }

      if (this.question?.state_id === 'INVIATO') {
        this.$emit('change-view', 'QuestionsToAssignView');
        return;
      }

      // Fallback al comportamento originale
      this.changeView(this.backView);
    },
    formatAsseverazioneState(question) {
      const isUOC = this.isUOCManager || this.isUOCMember;

      /*if (!isUOC) return null;*/
      if (question.state_id !== 'ASSEVERATO') return null;

      return {
        label: 'ASSEVERATO',
        published: !!question.published,
      };

    },
    confirmMove(direction) {
      this.moveDirection = direction;
      this.showMoveModal = true;
    },
    showSuccessModalMessage(message) {
      this.toastTitle = 'Successo';
      this.toastMessage = message;
      this.toastError = false;
    },

    showErrorModalMessage(message) {
      this.toastTitle = 'Errore';
      this.toastMessage = message;
      this.toastError = true;
    },
    async handleMove(choice) {
      if (choice !== 'Conferma') {
        this.moveDirection = null;
        return;
      }

      try {
        await this.$root.$api.post('/uoc/questions/move', {
          question_ids: [this.question.id],
          direction: this.moveDirection
        });

        const direction = this.moveDirection;
        this.showMoveModal = false;
        this.moveDirection = null;

        //await this.reloadQuestion();
        this.question.is_in_sala_uoc = direction === 'to_uoc' ? true : false;

        this.showSuccessToast("Successo", "Quesito spostato correttamente");
      } catch (err) {
        console.error(err);
        this.showErrorToast("Errore", "Impossibile spostare il quesito");
      } finally {
        this.moveDirection = null;
      }
    },
    async handleUOCDecision(choice) {

      if (choice !== 'Conferma') {
        this.pendingUOCDecision = null;
        console.log('parere', this.opinionText);
        return;
      }
      this.isLoading = true;
      await this.$nextTick();
      const parere = this.$refs.opinionEditor?.editor?.getHTML?.() || this.opinionText;
      const realOriginalText =
        this.$refs.originalTextEditor?.editor?.getHTML?.() || this.originalTextEditable;
      console.log('parere', parere);
      await this.saveArticlesAndTopics();

      try {
        const payload = {
          decision: this.pendingUOCDecision,
          opinion: parere,
          original_text: realOriginalText
        };
        console.log('parere pre salvataggio', parere);

        const response = await this.$root.$api.post(
          `/uoc/questions/${this.question.id}/decision`,
          payload
        );

        this.showUOCDecisionModal = false;
        this.pendingUOCDecision = null;

        this.toastTitle = 'Operazione effettuata';
        this.toastMessage = response.data.message || 'Operazione effettuata correttamente';
        this.toastError = false;

        //await this.reloadQuestion();

        this.question.state_id = response.data.question.state_id;
        this.question.updated_at = response.data.question.updated_at;
        this.question.published = response.data.question.published;
        if (this.question.decided_by && this.isRevisingAsseveration) {
          this.question.decided_by.id = this.$root.user.id;
          this.question.decided_by.name = this.$root.user.first_name;
          this.question.decided_by.surname = this.$root.user.last_name;
        }
        else {
          this.question.decided_by = {
            id: this.$root.user.id,
            name: this.$root.user.first_name,
            surname: this.$root.user.last_name
          };
        }
        this.question.decided_on = new Date().toISOString();
        this.question.opinion = response.data.question.opinion;
        this.question.originalText = realOriginalText;
        this.isRevisingAsseveration = false;

      } catch (error) {
        console.error(error);
        this.toastTitle = 'Errore';
        this.toastMessage = error.response?.data?.message || 'Errore durante l\'operazione';
        this.toastError = true;
      }
      this.isLoading = false;
    },
    confirmUOCDecision(type) {
      this.pendingUOCDecision = type;
      this.showUOCDecisionModal = true;
    },
    async reassignExpert() {
      if (!this.question.expert_id) return;

      this.isChangingState = true;
      const payload = {
        id: this.question.id,
        expert_id: this.question.expert_id,
        state_id: 'ASSEGNATO'
      };

      try {
        const response = await this.$root.$api.put(`/questions/${this.question.id}`, payload);
        this.question.state_id = response.data.question.state_id;
        this.showSuccessToast('Successo', 'Quesito riassegnato correttamente');
      } catch (err) {
        console.error(err);
        this.showErrorToast('Errore', 'Impossibile riassegnare il quesito');
      } finally {
        this.isChangingState = false;
      }
    },
    async reassignExpertFromDetail() {
      if (!this.selectedReassignExpertId) return;

      this.isChangingState = true;
      try {
        const payloadAssign = {
          question_id: this.question.id,
          legal_expert_user_id: this.selectedReassignExpertId
        };

        await this.$root.$api.post(`/questions/${this.question.id}/assign`, payloadAssign);

        const expert = this.expertsList.find(e => e.id === this.selectedReassignExpertId);
        if (expert) {
          this.question.expert = expert;
          this.question.expert_id = expert.id;
        }
        this.question.state_id = 'ASSEGNATO';

        this.showSuccessToast('Successo', 'Esperto riassegnato correttamente');
      } catch (err) {
        console.error(err);
        this.showErrorToast('Errore', 'Impossibile riassegnare l\'esperto');
      } finally {
        this.isChangingState = false;
      }
    },
    async exportCommentsPdf() {
      try {
        const response = await this.$root.$api.get(`/user/pdf-download-comments/${this.question.id}`, {
          responseType: 'blob'
        });

        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Commenti_Quesito_${this.question.id}.pdf`);
        document.body.appendChild(link);
        link.click();


        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);

      } catch (error) {
        console.error('Errore durante l\'esportazione del PDF:', error);
        this.$toast?.error?.('Errore durante l\'esportazione del PDF');
      }
    },
    applyFilterFromParams(filters) {
      this.selectedTopicsIds = filters.topics || [];
      this.selectedArticlesIds = filters.articles || [];
      this.topicSearch = filters.searchTopic || '';
      this.articleSearch = filters.searchArticle || '';
    },
    goBackWithFilters() {

      if (navigationHistory.hasHistory()) {
        const previousView = navigationHistory.peek();

        if (previousView && (previousView.viewName === 'QuestionsView' || previousView.viewName === 'QuestionsToAssignView')) {
          this.$emit('change-view', previousView.viewName, previousView.elementId);
          return;
        }
      }
      if (this.question?.state_id === 'INVIATO') {
        this.$emit('change-view', 'QuestionsToAssignView');
        return;
      }


      const filters = {
        topics: this.selectedTopicsIds,
        articles: this.selectedArticlesIds,
        searchTopic: this.topicSearch,
        searchArticle: this.articleSearch,
      };

      this.$router.push({
        name: 'questions.list',
        query: { filters: JSON.stringify(filters) }
      });
    },
    changeView(viewName) {
      console.log('Evento ricevuto:', viewName);
      this.$emit('change-view', viewName);
    },
    confirmDeleteComment(item) {
      this.commentToDelete = item;
      this.showDeleteModal = true;
    },

    async handleDeleteChoice(choice) {
      if (choice === 'Si, elimino' && this.commentToDelete) {
        try {
          await this.$root.$api.delete(`/questions/${this.question.id}/comments/${this.commentToDelete.id}`);
          this.comments = this.comments.filter(c => c.id !== this.commentToDelete.id);
          this.showSuccessToast('Successo', 'Commento eliminato');
        } catch (e) {
          console.error(e);
          this.showErrorToast('Errore', 'Impossibile eliminare il commento');
        }
      }
      // reset
      this.commentToDelete = null;
      this.showDeleteModal = false;
    },
    cancelCommentEdit() {
      this.showCommentEditor = false;
      this.isIntegrationMode = false;
      this.commentDraft = '';
      this.editingCommentId = null;
    },
    getInitialsFromOrg(org) {
      if (!org) return '';

      const stopWords = [
        'di', 'del', 'della', 'delle', 'dei', 'degli',
        'e', 'ed', 'da', 'in', 'su', 'per', 'con'
      ];

      return org
        .split(' ')
        .filter(word => word.length > 1)
        .filter(word => !stopWords.includes(word.toLowerCase()))
        .map(word => word[0])
        .slice(0, 2)
        .join('')
        .toUpperCase();
    },
    openCommentEditor(isIntegration = false) {
      this.isIntegrationMode = isIntegration;
      this.showCommentEditor = true;
      this.commentDraft = '';
      this.editingCommentId = null;
      this.$nextTick(() => {
      });
    },
    async submitComment() {
      if (!this.commentDraft || !this.commentDraft.trim()) return;


      try {
        if (this.editingCommentId) {
          //const payload = { text: this.commentDraft };
          //const resp = await this.$root.$api.put(`/questions/${this.question.id}/comments/${this.editingCommentId}`, payload);
          const idx = this.comments.findIndex(c => c.id === this.editingCommentId);
          /**if (idx !== -1) this.comments.splice(idx, 1, { ...resp.data, expanded: false });
          this.showSuccessToast('Successo', 'Commento modificato');*/
          if (idx === -1) return;

          const existingComment = this.comments[idx];

          const payload = {
            text: this.commentDraft,
            type_id: existingComment.type_id
          };

          const resp = await this.$root.$api.put(
            `/questions/${this.question.id}/comments/${this.editingCommentId}`,
            payload
          );

          this.comments[idx] = {
            ...existingComment,
            ...resp.data,
            expanded: false
          };

          this.showSuccessToast('Successo', 'Commento modificato');
        } else {
          const type_id = this.canShowIntegration && this.question.state_id !== 'ASSEGNATO' && this.question.state_id !== 'INVIATO'
            ? 'I'
            : this.canShowComment
              ? 'C'
              : null;

          if (!type_id) {
            this.showErrorToast('Errore', 'Non hai i permessi per aggiungere commenti o integrazioni');
            return;
          }

          const payload = { text: this.commentDraft, type_id: type_id };
          const resp = await this.$root.$api.post(`/questions/${this.question.id}/comments`, payload);
          this.comments.push({ ...resp.data, expanded: false });
          this.showSuccessToast('Successo', 'Commento aggiunto');
        }
        this.cancelCommentEdit();
      } catch (e) {
        console.error(e);
        this.showErrorToast('Errore', 'Impossibile salvare il commento');
      }
    },
    startEditComment(item) {
      this.editingCommentId = item.id;
      this.commentDraft = item.text || item.html || '';
      this.isIntegrationMode = item.type_id === 'I';
      this.showCommentEditor = true;
    },
    async deleteComment(item) {
      if (!confirm('Confermi eliminazione commento?')) return;
      try {
        await this.$root.$api.delete(`/questions/${this.question.id}/comments/${item.id}`);
        this.comments = this.comments.filter(c => c.id !== item.id);
        this.showSuccessToast('Successo', 'Commento eliminato');
      } catch (e) {
        console.error(e);
        this.showErrorToast('Errore', 'Impossibile eliminare il commento');
      }
    },
    handleConfirmChoice(choice) {
      this.showConfirmModal = false;
      if (choice === 'Invia') {
        this.replyToQuestionConfirmed();
      }
    },
    async saveDraft() {
      this.isChangingState = true;
      await this.$nextTick();
      const realAnswer = this.$refs.answerEditor?.editor?.getHTML?.() || this.answerText;
      const realEditableQuestion = this.$refs.editableEditor?.editor?.getHTML?.() || this.editableQuestion;
      const payload = {
        id: this.question.id,
        text: realEditableQuestion,
        answer: realAnswer,
        topics: this.topicsSnapshot.map(t => t.id),
        //articles: this.articlesSnapshot.map(a => a.originalId),
        articles: this.articlesSnapshot
          .filter(a => !a.isSection)
          .map(a => a.originalId),
        state_id: 'IN_LAVORAZIONE',
        opinion: this.opinionText
      };

      try {
        const response = await this.$root.$api.put(`/questions/${this.question.id}`, payload);
        this.question.updated_at = response.data.question.updated_at;
        this.question.answer = realAnswer;
        this.showSuccessToast("Successo", "Salvato in bozza");
        this.question.state_id = "IN_LAVORAZIONE";
        this.isDraftSaved = true;
      } catch (err) {
        this.showErrorToast("Errore", "Impossibile salvare in bozza");
      } finally {
        this.isChangingState = false;
      }
    },
    /**async autosaveQuestion() {
      const payload = {
        id: this.question.id,
        text: this.editableQuestion,
        answer: this.answerText,
        topics: this.topicsSnapshot.map(t => t.id),
        articles: this.articlesSnapshot.map(a => a.id),
        autosave: true
      };
  
      try {
        await this.$root.$api.put(`/questions/${this.question.id}/autosave`, payload);
        console.log('Autosave completato');
      } catch (e) {
        console.warn('Errore autosave', e);
      }
    },*/
    async saveArticlesAndTopics() {
      if (!this.question?.id) return;

      this.isChangingState = true;

      const payload = {
        id: this.question.id,
        topics: this.topicsSnapshot.map(t => t.id),
        //articles: this.articlesSnapshot.map(a => a.id),
        articles: this.articlesSnapshot
          .filter(a => !a.isSection)
          .map(a => a.originalId),
      };

      try {
        const response = await this.$root.$api.put(`/questions/${this.question.id}`, payload);
        this.question.updated_at = response.data.question.updated_at;
        this.showSuccessToast("Successo", "Articoli e argomenti salvati correttamente");
      } catch (err) {
        console.error(err);
        this.showErrorToast("Errore", "Impossibile salvare articoli/argomenti");
      } finally {
        this.isChangingState = false;
      }
    },
    async autosaveBeforeUnload() {
      if (!['RISPOSTA_IN_BOZZA', 'IN_LAVORAZIONE'].includes(this.question.state_id) || !this.isAssigned) {
        console.log('Autosave saltato: stato non consentito', this.question.state_id);
        return;
      }
      const payload = {
        id: this.question.id,
        text: this.editableQuestion,
        answer: this.answerText,
        topics: this.topicsSnapshot.map(t => t.id),
        //articles: this.articlesSnapshot.map(a => a.id),
        articles: this.articlesSnapshot
          .filter(a => !a.isSection)
          .map(a => a.originalId),
        //state_id: 'RISPOSTA_IN_BOZZA',
        state_id: 'IN_LAVORAZIONE',
        autosave: true
      };

      const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
      /**const ok = navigator.sendBeacon(
        `${this.$root.config.VUE_APP_API_URL}/questions/${this.question.id}/autosave`,
        blob
      );*/

      await this.$root.$api.post(`/questions/${this.question.id}/autosave`, payload);

      console.log('Autosave completato');


    },
    toggleLeftPanelQuesito() {
      this.leftPanelVisibleQuesito = !this.leftPanelVisibleQuesito;
    },
    closeArticleModal() {
      this.showArticleModal = false;
      this.localElementId = null;
      this.modalArticleId = null;
      this.modalVersion = null;
      this.stableQuestionId = this.question.id;
      this.$emit('change-view', 'QuestionDetailExpertView', this.question.id)
      this.$emit('reset-element');

      //this.$emit('reset-original-element', this.stableQuestionId);
    },
    async replyToQuestionConfirmed() {
      this.isChangingState = true;

      await this.$nextTick();
      const realAnswer = this.$refs.answerEditor?.editor?.getHTML?.() || this.answerText;
      const realEditableQuestion = this.$refs.editableEditor?.editor?.getHTML?.() || this.editableQuestion;
      const payload = {
        id: this.question.id,
        text: realEditableQuestion,
        answer: realAnswer,
        topics: this.topicsSnapshot.map(t => t.id),
        //articles: this.articlesSnapshot.map(a => a.id),
        articles: this.articlesSnapshot
          .filter(a => !a.isSection)
          .map(a => a.originalId),
        state_id: 'EVASO',
      };

      console.log('Payload:', payload);

      try {
        const response = await this.$root.$api.put(`/questions/${this.question.id}`, payload);

        // Aggiorna dati locali
        this.question.state_id = response.data.question.state_id;
        this.question.updated_at = response.data.question.updated_at;

        // Disabilita pulsanti
        this.isAnswered = true;
        this.question.answer = realAnswer;
        this.question.sending_user = {
          id: this.$root.user.id,
          name: this.$root.user.name,
          surname: this.$root.user.last_name
        };
        this.question.sending_date = new Date().toISOString();
        // Mostra toast di successo
        //this.showSuccessToast("Operazione completata", "Quesito evaso correttamente");

        this.showSuccessModal = true;

      } catch (err) {
        console.error(err);
        this.showErrorToast("Errore", "Impossibile evadere il quesito");
      } finally {
        this.isChangingState = false;
      }
    },
    replyToQuestion() {
      const questionPayload = {
        id: this.question.id,
        text: this.editableQuestion,
        answer: this.answerText,
        topics: this.topicsSnapshot.map(t => ({ id: t.id, title: t.title })),
        articles: this.articlesSnapshot.map(a => ({
          id: a.id,
          title: a.versions?.[a.versions.length - 1]?.title || a.title
        })),
        expert_id: this.question.expert_id,
        state_id: 'INVIATO'
      };

      console.log('Question payload:', questionPayload);
    },
    getInitials(user) {
      if (!user || !user.name) return '';
      return `${user.name[0] || ''}${user.surname[0] || ''}`.toUpperCase();
    },
    formatDate(date) {
      const d = new Date(date);
      return d.toLocaleDateString() + ' ' + d.toLocaleTimeString();
    },
    toggleComment(comment) {
      comment.expanded = !comment.expanded;
    },
    needsReadMore(comment) {
      return comment.text.length > 250;
    },

    openModalArticle() {
      const articleId = this.$route.params.article;
      const version = this.$route.params.version || null;

      this.modalArticleId = articleId;
      this.modalVersion = version;
      this.modalCounter++;
      //this.showArticleModal = true;

      this.$nextTick(() => {
        this.showArticleModal = true;
      });
    },
    async reloadQuestion() {
      this.isLoading = true;
      try {
        //this.availableArticles = this.extractAllArticlesFromTree();
        await this.waitForRootContents();
        this.availableArticles = this.extractAllArticlesAndSections();
        await this.loadElements();
        await this.loadTopics();
        await this.loadQuestion();
        /**this.$nextTick(() => {
          this.availableArticles = this.extractAllArticlesFromTree();
          console.log("reloadQuestion 2");
        });*/
        console.log("reloadQuestion");
      } finally {
        this.isLoading = false;
      }
    },
    getArticleDisplayTitle(article) {
      if (!article) return '—';
      let title = '';

      if (article.versions && article.versions.length > 0) {
        title = article.versions[article.versions.length - 1].title || '';
      } else {
        title = article.title || '';
      }

      return this.formatArticleTitle(title);
    },
    getArticleDisplayTitleTooltip(article) {
      if (!article) return '—';
      let title = '';

      if (article.versions && article.versions.length > 0) {
        title = article.versions[article.versions.length - 1].title || '';
      } else {
        title = article.title || '';
      }

      return title;
    },
    extractAllArticlesFromTree() {
      const all = this.$root.contents?.articles || {};

      return Object.values(all)
        .filter(a => !a.deleted_at && a.versions && a.versions.length > 0)
        .map(a => ({
          id: a.id,
          versions: a.versions
        }));
    },
    async loadTopics() {
      try {
        const [topicsResponse] = await Promise.all([
          this.$root.$api.get('/questions/topics'),
        ]);
        this.availableTopics = topicsResponse.data;
      } catch (error) {
        this.showErrorToast('Errore', 'Impossibile caricare argomenti o legislazioni');
      }
    },

    getTopicName(id) {
      const topic = this.availableTopics.find(t => t.id === id);
      return topic ? topic.title : '';
    },
    toggleLeftPanel() {
      this.leftPanelVisible = !this.leftPanelVisible;
    },
    formatArticleTitle(title) {
      if (!title) return '—';

      const match = title.match(/^Art\.\s*\d+/);
      if (match) return match[0];

      if (/^Articolo\s+\d+/i.test(title)) return title;

      return title;
    },
    removeArticle(index) {
      const removed = this.articlesSnapshot[index].id;
      this.articlesSnapshot.splice(index, 1);
      this.selectedArticlesIds = this.selectedArticlesIds.filter(id => id !== removed);
      this.question.articles = [...this.selectedArticlesIds];
    },
    removeTopic(index) {
      const removed = this.topicsSnapshot[index].id;
      this.topicsSnapshot.splice(index, 1);
      this.selectedTopicsIds = this.selectedTopicsIds.filter(id => id !== removed);
      this.question.topics = [...this.selectedTopicsIds];
    },
    formatState(state) {
      const map = {
        INVIATO: { label: "RICEVUTO", color: "yellow" },
        ASSEGNATO: { label: "ASSEGNATO", color: "blue" },
        IN_LAVORAZIONE: { label: "IN LAVORAZIONE", color: "purple" },
        RISPOSTA_IN_BOZZA: { label: "BOZZA", color: "gray" },
        EVASO: { label: "EVASO", color: "green" },
        IN_ESAMINA_UOC: { label: "ASSEGNATO A UOC", color: "yellow" },
        ASSEVERATO: { label: "ASSEVERATO", color: "green" },
        NON_ASSEVERATO: { label: "NON ASSEVERATO", color: "red" },
        STORICO: { label: "STORICO", color: "black" },
      };
      return map[state] || { label: state, color: "gray" };
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' });
    },
    async loadQuestion() {
      if (!this.elementId || this.elementId === "0") {
        return;
      }
      const idToLoad = this.stableQuestionId || this.elementId;

      const response = await this.$root.$api.get(`/questions/${idToLoad}`);
      const q = response.data;

      const articlesArray = this.$root.contents?.articles || [];

      q.articles = (q.articles || []).map(a => {
        const content = articlesArray.find(c => c.id === a.id);

        if (!content || !content.versions?.length) {
          return { ...a, title: null };
        }

        const lastVersion = content.versions.reduce(
          (prev, curr) => (curr.id > prev.id ? curr : prev),
          content.versions[0]
        );

        return { ...a, title: lastVersion.title };
      });

      this.selectedTopicsIds = q.topics?.map(t => t.id) || [];
      this.selectedArticlesIds = (q.articles?.map(a => 'article-' + a.id)) || [];


      this.articlesSnapshot = this.availableArticles.filter(a =>
        !a.isSection && this.selectedArticlesIds.includes(a.id)
      );

      this.question = q;
      this.question.expert_id = q.expert?.id ?? null;
      this.selectedReassignExpertId = this.question.expert_id;

      this.topicsSnapshot = q.topics?.map(t => ({ ...t })) || [];
      this.articlesSnapshot = q.articles?.map(a => ({ ...a })) || [];
      this.originalTextEditable = q.originalText || '';

      this.revisionText = this.question.text || '';

      this.$nextTick(() => {
        this.answerText = q.answer || '';
        this.opinionText = q.opinion || '';
        this.response = q.answer || '';
        this.originalTextEditable = q.originalText || '';
      });

      this.comments = (q.comments || [])
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .map(c => ({
          ...c,
          expanded: false
        }));
    },

    async loadElements() {
    }

  },
  async mounted() {
    const params = this.$root.params || {};
    const filters = params.filters || null;
    const response = await this.$root.$api.get('/users/legal');
    this.expertsList = response.data;
    await this.reloadQuestion();
    if (!this.stableQuestionId) {
      this.stableQuestionId = this.localElementId;
    }

    if (filters) {
      this.applyFilterFromParams(filters);
    }

    this.checkArticlesHeight();
    window.addEventListener('resize', this.checkArticlesHeight);

    /**this.autosaveInterval = setInterval(() => {
      this.saveDraft();
    }, 1500000);*/

    window.addEventListener('beforeunload', this.autosaveBeforeUnload);
    /**this.isLoading = true;
    try {
      this.availableArticles = this.extractAllArticlesFromTree()
      await this.loadElements();
      await this.loadTopics();
      await this.loadQuestion();
    } finally {
      this.isLoading = false;
    }*/
  },

  beforeUnmount() {
    clearInterval(this.autosaveInterval);
    window.removeEventListener('resize', this.checkArticlesHeight);
    window.removeEventListener('beforeunload', this.autosaveBeforeUnload);
  }
};
</script>

<style scoped>
.question-detail-expert {
  height: 88vh !important;
  display: flex;
  gap: 1rem;
  background-color: rgba(255, 255, 255, 0.7);
  padding: 1rem;
  border-radius: 8px;
}

.left-panel {
  max-width: 25%;
  overflow-y: auto;
}

.right-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  background: linear-gradient(180deg,
      #fff1f7 0%,
      #fff6fa 100%);
}

.question-body {
  flex: 1 1 0;
  display: flex;
  gap: 1rem;
  min-height: 0;
}

.question-body>div {
  min-width: 0 !important;
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: #fff;
  /* bianco per contrasto con rosa */
  border-radius: 8px;
  padding: 0.75rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #ddd;
  min-height: 0;
}

.quesito-panel,
.quesito-panel>.no-grow,
.right-panel {
  min-width: 0 !important;
}

.question-body>div .fw-bold {
  margin-bottom: 0.5rem;
  font-size: 1rem;
  color: #333;
}

.question-body>div .ql-editor {
  min-height: 150px;
  /* altezza minima editor */
  background-color: rgba(255, 255, 255, 0.7);
  /* sfondo editor */
  border-radius: 4px;
  padding: 0.5rem;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}

.loading-text {
  margin-top: 1rem;
  font-weight: bold;
}

.status-label {
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 1.1rem;
  white-space: nowrap;
  color: white;
}

.status-box {
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.75rem;
  color: white;
  font-weight: 600;
}

.status-yellow {
  background: #ffc107;
  color: #fff;
}

.status-green {
  background-color: green;
  color: white;
}

.status-red {
  background-color: red;
  color: white;
}

.status-blue {
  background: #0d6efd;
  color: #fff;
}

.status-purple {
  background: #6f42c1;
  color: #fff;
}

.status-gray {
  background: #6c757d;
  color: #fff;
}

.small-rect-btn {
  height: 1rem;
  width: 1.5rem;
  padding: 0;
  font-size: 0.8rem;
  border-radius: 3rem;
  display: flex;
  justify-content: center;
  align-items: center;
}

.tag-row {
  display: flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  flex-wrap: nowrap;
}

.tag-box {
  /**background-color: #e9ecef;*/
  background-color: grey;
  border: 1px solid #d3d3d3;
  padding: 0px 8px;
  border-radius: 6px;
  font-size: 0.7rem;
  white-space: nowrap;
  color: white;
}

.remove-btn {
  background-color: #dc3545;
  color: white;
  border: none;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  padding: 0;
  line-height: 1;
  font-size: 0.9rem;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
}

.remove-btn:hover {
  background-color: #b02a37;
}

.ql-editor {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important
}

.toggle-pill {
  position: relative;
  width: 220px;
  height: 50px;
  user-select: none;
}

.toggle-pill input.toggle-switch {
  display: none;
  /* nascondiamo checkbox reale */
}

.toggle-pill .toggle-label {
  display: block;
  width: 22rem;
  height: 2rem;
  background-color: #e9ecef;
  border-radius: 50px;
  position: relative;
  cursor: pointer;
  overflow: hidden;
  border: 2px solid #0d6efd;
}

.toggle-pill .toggle-option {
  position: absolute;
  width: 50%;
  height: 100%;
  top: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 600;
  color: white;
  /* testo non selezionato nero */
  transition: color 0.3s;
  pointer-events: none;
  /* clicca solo sul label */
}

.toggle-pill .toggle-left {
  left: 0;
}

.toggle-pill .toggle-right {
  right: 0;
}

.toggle-pill .toggle-label::before {
  content: '';
  position: absolute;
  width: 50%;
  height: 100%;
  top: 0;
  left: 0;
  background-color: #0d6efd;
  /* colore opzione attiva */
  border-radius: 50px;
  transition: left 0.3s;
}

.toggle-pill input.toggle-switch:checked+.toggle-label::before {
  left: 50%;
}

.toggle-pill input.toggle-switch:checked+.toggle-label .toggle-left {
  color: black;
  /* opzione selezionata */
}

.toggle-pill input.toggle-switch:not(:checked)+.toggle-label .toggle-right {
  color: black;
  /* opzione selezionata */
}

.toggle-3d {
  position: relative;
  width: 240px;
  height: 31px;
  perspective: 500px;
}

.toggle-3d input.toggle-switch {
  display: none;
}

.toggle-3d .toggle-label {
  display: block;
  width: 22rem;
  height: 2rem;
  background-color: #e9ecef;
  border-radius: 50px;
  padding: 5px;
  /* spazio interno per effetto “toggle flottante” */
  box-shadow: inset 0 4px 6px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.2);
  position: relative;
  cursor: pointer;
  overflow: hidden;
  border: 2px solid #0A2342;
}

.toggle-3d .toggle-option {
  position: absolute;
  width: 50%;
  height: 100%;
  top: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 600;
  color: white;
  transition: color 0.3s;
  pointer-events: none;
}

.toggle-3d .toggle-left {
  left: 0;
}

.toggle-3d .toggle-right {
  right: 0;
}

.toggle-3d .toggle-label::before {
  content: '';
  position: absolute;
  width: calc(50% - 10px);
  /* più piccolo del 50% per lasciare spazio bordo */
  height: calc(100% - 10px);
  top: 5px;
  left: 5px;
  background-color: #0A2342;
  border-radius: 50px;
  box-shadow: 0 6px 8px rgba(0, 0, 0, 0.2);
  transition: left 0.3s;
}

.toggle-3d input.toggle-switch:checked+.toggle-label::before {
  left: calc(50% + 5px);
}

.toggle-3d input.toggle-switch:checked+.toggle-label .toggle-left {
  color: black;
}

.toggle-3d input.toggle-switch:not(:checked)+.toggle-label .toggle-right {
  color: black;
}

.editor-container {
  min-height: 150px;
  width: 100%;
}

.compact-view section.container {
  padding: 0 !important;
  margin: 0 !important;
  max-width: 100% !important;
}

.compact-view .row {
  margin: 0 !important;
}

.compact-view .col-12 {
  padding: 0.25rem !important;
}

.compact-view .m-2 {
  margin: 0.35rem !important;
}

.compact-view .p-3 {
  padding: 0.75rem !important;
}

.articles-left * {
  margin-bottom: 0.35rem !important;
}

.articles-left .m-2 {
  margin: 0.5rem 0 !important;
}

.articles-left .p-3 {
  padding: 0.5rem !important;
}

.scaled-articles {
  transform: scale(0.75);
  transform-origin: top left;
  width: 133%;
}

.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(-100%);
  opacity: 0;
}

.left-toggle {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  background-color: #f8f9fa;
  border-radius: 4px;
  margin-right: 4px;
  user-select: none;
}

.argomenti-container {
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-width: 100%;
}

.tag-row-wrapper {
  display: flex;
  gap: 6px;
  margin-bottom: 4px;
  flex-wrap: wrap;
}

.tag-row {
  display: flex;
  align-items: center;
  background: #f1f1f1;
  padding: 4px 8px;
  border-radius: 4px;
  white-space: nowrap;
}

.tag-box {
  margin-right: 4px;
}

.comments-view {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.comment-postit {
  background-color: #fff9c4;
  /* giallo post-it */
  padding: 1rem;
  border-radius: 8px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  max-width: 100%;
}

.comment-header .avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #fbc02d;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
}

.comment-text.collapsed {
  display: -webkit-box;
  -webkit-line-clamp: 5;
  -webkit-box-orient: vertical;
  overflow: hidden;
  display: box;
  box-orient: vertical;
  line-clamp: 5;
  overflow: hidden;
}

.comment-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: red;
  color: white;
  font-size: 0.8rem;
  margin-left: 4px;
}

.tiptap-editor {
  height: 100%;
}

.editor-wrapper {
  flex: 1 1 0;
  min-height: 0;
}

.editor-content {
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;

  flex-grow: 1;
  min-height: 150px;
  padding: 8px;
  border: 1px solid #eee;
  border-radius: 4px;
  background: #fff !important;
  outline: none !important;
  box-shadow: none !important;
}

.original-box {
  white-space: pre-wrap;
  word-wrap: break-word;
  font-size: 16px;
  overflow-y: auto;
  overflow-x: hidden;
  background-color: #fff;
  flex: 1;
}

.quesito-panel,
.risposta-panel,
.tiptap-wrapper .ProseMirror {
  white-space: pre-wrap !important;
  /* va a capo sui ritorni a capo */
  word-break: break-word !important;
  /* forza a capo parole lunghe */
  overflow-wrap: break-word !important;
  /* compatibilità browser */
  min-width: 0 !important;
  /* permette al flex di restringersi */
}

.no-grow {
  min-width: 0 !important;
  max-width: 100% !important;
}

.tiptap,
.tiptap * {
  overflow-wrap: break-word !important;
  word-break: break-word !important;
  white-space: pre-wrap !important;
}

.tiptap-wrapper {
  min-width: 0 !important;
  max-width: 100% !important;
}

.comment-integration {
  background-color: #dbeafe;
  /* blu chiaro soft */
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  position: relative;
}

.comment-integration .thread-header {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

.comment-integration .avatar {
  width: 32px;
  height: 32px;
  background-color: #2563eb;
  /* blu più deciso per contrasto */
  color: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  margin-right: 0.5rem;
  font-size: 0.9rem;
}

.comment-integration .user-name {
  font-weight: 600;
}

.comment-integration .comment-date {
  font-size: 0.75rem;
  color: #1e40af;
  /* blu scuro per armonia */
}

.comment-integration .thread-content {
  margin-bottom: 0.5rem;
  display: inline !important;
}

.comment-integration .thread-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.comment-yellow {
  background-color: #fff3b0;
  /* giallo chiaro come post-it */
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  position: relative;
}

.comment-yellow .thread-header {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

.comment-yellow .avatar {
  width: 32px;
  height: 32px;
  background-color: #6c757d;
  color: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  margin-right: 0.5rem;
  font-size: 0.9rem;
}

.comment-yellow .user-name {
  font-weight: 600;
}

.comment-yellow .comment-date {
  font-size: 0.75rem;
  color: #6c757d;
}

.comment-yellow .thread-content {
  margin-bottom: 0.5rem;
  display: inline !important;
}

.comment-yellow .thread-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.comment-date {
  font-size: 0.75rem;
  color: #6c757d;
}

.thread-header-common {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

.thread-header-common .avatar {
  width: 32px;
  height: 32px;
  background-color: #6c757d;
  /* Colore avatar */
  color: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  margin-right: 0.5rem;
  font-size: 0.9rem;
}

.thread-header-common .user-name {
  font-weight: 600;
}

.thread-header-common .comment-date {
  font-size: 0.75rem;
  color: #6c757d;
}

/* Background e bordo colorato per tipo */
.thread-item-original {
  background-color: #e0f7fa;
  border-left: 4px solid #00acc1;
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
}

.thread-item-answer-draft {
  background-color: #e0f7fa;
  border-left: 4px solid #00acc1;
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
}

.thread-item-revision {
  background-color: #e0f7fa;
  border-left: 4px solid #00acc1;
  /**background-color: #f3e5f5;
  border-left: 4px solid #8e24aa;*/
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
}

.thread-item-opinion {
  background-color: #d8f7d7;
  border-left: 4px solid green;
  /**background-color: #f3e5f5;
  border-left: 4px solid #8e24aa;*/
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
}

.thread-item-answer {
  background-color: #e0f7fa;
  border-left: 4px solid #00acc1;
  /**background-color: #fff9c4;
  border-left: 4px solid #fbc02d;*/
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
}

.thread-label {
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.15rem 0.5rem;
  border-radius: 0.25rem;
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  color: white;
}

/** .label-original {
  background-color: #00acc1;
}

.label-revision {
  background-color: #8e24aa;
}

.label-answer {
  background-color: #fbc02d;
  color: #333;
}*/

.label-original {
  background-color: black;
  color: white;
}

.label-revision {
  background-color: black;
  color: white;
}

.label-answer {
  background-color: black;
  color: white;
}

.label-opinion {
  background-color: black;
  color: white;
}

.thread-item {
  position: relative;
}

.thread-content ::v-deep(mark) {
  display: inline !important;
  margin: 0;
  padding: 0;
  line-height: inherit;
}

.thread-content ::v-deep(pre) {
  white-space: pre-wrap;
  word-break: break-word;
  overflow-x: auto;
  max-width: 100%;
}

.thread-content ::v-deep(code) {
  white-space: pre-wrap;
  word-break: break-word;
}

.mark,
mark {
  display: inline !important;
  padding: .1875em;
  color: var(--bs-highlight-color);
  background-color: var(--bs-highlight-bg);
}
</style>
<style>
.mark,
mark {
  display: inline !important;
  padding: 0.1875em;
  color: var(--bs-highlight-color);
  background-color: var(--bs-highlight-bg);
}

.asseverazione-wrapper {
  display: flex;
  justify-content: flex-end;
  /* allinea tutto a destra */
  align-items: center;
  gap: 4px;
  /* distanza minima tra badge e cerchio */
}

.publish-indicator {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  font-size: 14px;
  font-weight: bold;
  line-height: 1;
}

.publish-indicator.published {
  background-color: #2e7d32;
  color: #fff;
}

.publish-indicator.not-published {
  background-color: #c62828;
  color: #fff;
}

.tag-list {
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  gap: 6px;
}

.tag-list-articles {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.tag-list-articles-wrapper {
  position: relative;
  width: 100%;
}

.tag-list-articles {
  transition: max-height 0.3s ease;
  overflow: hidden;
}

.tag-list-articles.collapsed {
  max-height: 32px;
}

.toggle-btn {
  background: none;
  border: none;
  cursor: pointer;
  margin-left: 8px;
  font-size: 1.1rem;
}

.integration-postit {
  background-color: #dbeafe;
  padding: 1rem;
  border-radius: 8px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

.bg-orange {
  background-color: #fd7e14 !important;
  color: #fff !important;
}

.status-black {
  background-color: #000;
  color: #fff;
}
</style>